import { useEffect, useState } from 'react';
import { useReveal } from '../hooks/useReveal.js';
import { AppleIcon, PlayIcon } from '../components/icons.js';
import { apiFetch } from '@/lib/api.js';
import { APP_STORE_URL, PLAY_STORE_URL } from '@/lib/appStores.js';

/**
 * Real customer reviews, mirrored nightly from the App Store and Google Play
 * into the API (GET /store-reviews). While the stores are young and no
 * reviews exist yet, the section quietly invites the reader to write the
 * first one instead of showing manufactured praise.
 */
type Review = {
  store: 'apple' | 'google';
  author: string | null;
  rating: number;
  title: string | null;
  body: string | null;
  reviewed_at: string | null;
};

const WRITE_REVIEW_APPLE = `${APP_STORE_URL}?action=write-review`;

function Stars({ rating }: { rating: number }) {
  return (
    <span
      className="text-gold"
      aria-label={`${rating} out of 5 stars`}
      style={{ letterSpacing: '0.15em' }}
    >
      {'★'.repeat(rating)}
      <span style={{ opacity: 0.35 }}>{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

function Card({ review, delay }: { review: Review; delay: number }) {
  const ref = useReveal<HTMLDivElement>(0.2);
  const storeName = review.store === 'apple' ? 'App Store' : 'Google Play';
  return (
    <div
      ref={ref}
      className="ow-reveal ow-voice-card bg-bg-elevated border border-border rounded-card flex flex-col"
      style={
        {
          padding: 36,
          ['--reveal-delay' as never]: `${delay}ms`,
        } as React.CSSProperties
      }
    >
      <Stars rating={review.rating} />
      {review.title && (
        <p
          className="font-display text-primary mt-3"
          style={{ fontSize: 15, fontWeight: 500, letterSpacing: '0.03em' }}
        >
          {review.title}
        </p>
      )}
      {review.body && (
        <p className="scripture text-primary mt-2" style={{ fontSize: 18, lineHeight: 1.55 }}>
          “{review.body}”
        </p>
      )}
      <span className="ow-voice-divider mt-6" />
      <p
        className="font-display text-primary mt-4"
        style={{ fontSize: 13, fontWeight: 500, letterSpacing: '0.04em' }}
      >
        {review.author || 'A reader'}
      </p>
      <p
        className="text-secondary flex items-center gap-2"
        style={{ fontSize: 12, lineHeight: 1.5 }}
      >
        {review.store === 'apple' ? <AppleIcon /> : <PlayIcon />} Review on the {storeName}
      </p>
    </div>
  );
}

export function Voices() {
  const headerRef = useReveal<HTMLDivElement>(0.3);
  const [reviews, setReviews] = useState<Review[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ items: Review[] }>('/store-reviews?min_rating=4&limit=6')
      .then((d) => {
        if (!cancelled) setReviews(d.items.filter((r) => r.body));
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="voices" className="relative w-full" style={{ padding: '76px 20px' }}>
      <div className="mx-auto max-w-container">
        <div ref={headerRef} className="ow-reveal text-center">
          <p
            className="font-display uppercase text-gold"
            style={{ fontSize: 11, letterSpacing: '0.32em' }}
          >
            § VI - Voices
          </p>
          <h2
            className="font-display text-primary mt-5"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 500,
              letterSpacing: '0.02em',
            }}
          >
            What others have said.
          </h2>
        </div>

        {reviews && reviews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            {reviews.map((r, i) => (
              <Card key={`${r.store}-${r.reviewed_at}-${i}`} review={r} delay={(i % 3) * 100} />
            ))}
          </div>
        ) : (
          <div className="text-center mt-14 mx-auto" style={{ maxWidth: 560 }}>
            <p className="scripture text-secondary" style={{ fontSize: 19, lineHeight: 1.6 }}>
              The first reviews are still being written. If the app serves your prayer life, a few
              honest words help others find the Way.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <a
                href={WRITE_REVIEW_APPLE}
                target="_blank"
                rel="noopener noreferrer"
                className="ow-cta-ghost"
              >
                Review on the App Store
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="ow-cta-ghost"
              >
                Review on Google Play
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
